import React, { useState } from 'react';
import { DollarSign, CheckCircle2, Calendar, CreditCard, X } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import Modal from '../../shared/components/Modal';

export default function RecordPaymentModal({ invoice, settings = {}, onSave, onClose }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const cur = settings?.currency || 'MAD';

  const totalTTC = Number(invoice.totalTTC || invoice.totalAmount) || 0;
  const currentPaid = Number(invoice.paidAmount) || 0;
  const remaining = Math.max(0, totalTTC - currentPaid);

  const [paymentAmount, setPaymentAmount] = useState(remaining > 0 ? remaining : 0);
  const [paymentMethod, setPaymentMethod] = useState(invoice.paymentMethod || 'Espèces');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(paymentAmount);
    if (isNaN(amt) || amt <= 0) {
      return alert(isAr ? 'يرجى إدخال مبلغ دفع صالح أكبر من الصفر' : 'Veuillez saisir un montant supérieur à 0.');
    }

    setSubmitting(true);
    try {
      const newPaidTotal = currentPaid + amt;
      const newRemaining = Math.max(0, totalTTC - newPaidTotal);
      
      let nextStatus = 'partial';
      if (newRemaining <= 0) {
        nextStatus = 'paid';
      }

      const paymentRecord = {
        id: `pay_${Date.now()}`,
        amount: amt,
        method: paymentMethod,
        date: paymentDate,
        notes: notes.trim(),
        createdAt: new Date().toISOString()
      };

      const updatedInvoice = {
        ...invoice,
        paidAmount: newPaidTotal,
        remainingAmount: newRemaining,
        paymentMethod: paymentMethod,
        status: nextStatus,
        paymentHistory: [...(invoice.paymentHistory || []), paymentRecord],
        updatedAt: new Date().toISOString()
      };

      await onSave(updatedInvoice);
      onClose();
    } catch (err) {
      alert('❌ ' + (err.message || 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={isAr ? 'تسجيل دفعة جديدة' : 'Enregistrer un paiement'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        
        {/* Invoice Summary Header */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 font-semibold">{isAr ? 'الفاتورة' : 'Facture'}</div>
            <div className="text-sm font-bold text-slate-900 font-mono">
              {invoice.invoiceNumber || String(invoice.id).substring(0, 8).toUpperCase()}
            </div>
            <div className="text-xs text-slate-600 font-medium mt-0.5">
              {invoice.companyName || invoice.clientName || '—'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 font-semibold">{isAr ? 'المتبقي للأداء' : 'Reste à payer'}</div>
            <div className="text-base font-bold text-amber-600 font-mono">
              {remaining.toFixed(2)} {cur}
            </div>
            <div className="text-[11px] text-slate-400">
              Total: {totalTTC.toFixed(2)} {cur}
            </div>
          </div>
        </div>

        {/* Payment Amount */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-slate-700">
              {isAr ? 'مبلغ الدفعة *' : 'Montant du versement *'}
            </label>
            {remaining > 0 && (
              <button
                type="button"
                onClick={() => setPaymentAmount(remaining)}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-semibold underline"
              >
                {isAr ? 'سداد المتبقي بالكامل' : 'Régler la totalité'}
              </button>
            )}
          </div>
          <div className="relative">
            <input
              type="number"
              min="0.01"
              max={remaining > 0 ? remaining : undefined}
              step="0.01"
              value={paymentAmount}
              onChange={e => setPaymentAmount(e.target.value)}
              className="w-full pl-3 pr-14 py-2.5 bg-white text-base font-bold text-slate-900 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 shadow-sm"
              required
            />
            <span className="absolute right-3 top-3 text-xs font-bold text-slate-400">
              {cur}
            </span>
          </div>
        </div>

        {/* Payment Method & Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              {isAr ? 'طريقة الأداء' : 'Mode de paiement'}
            </label>
            <select
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 bg-white text-sm font-semibold border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
            >
              <option value="Espèces">{isAr ? '💵 نقداً (Espèces)' : '💵 Espèces'}</option>
              <option value="Chèque">{isAr ? '📄 شيك (Chèque)' : '📄 Chèque'}</option>
              <option value="Virement">{isAr ? '🏦 تحويل بنكي (Virement)' : '🏦 Virement bancaire'}</option>
              <option value="Carte">{isAr ? '💳 بطاقة بنكية (Carte)' : '💳 Carte bancaire'}</option>
              <option value="Autre">{isAr ? '🏷️ أخرى (Autre)' : '🏷️ Autre'}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              {isAr ? 'تاريخ الدفعة' : 'Date de paiement'}
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={e => setPaymentDate(e.target.value)}
              className="w-full px-3 py-2 bg-white text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            {isAr ? 'ملاحظات (رقم الشيك، رقم التحويل...)' : 'Notes / Référence (optionnel)'}
          </label>
          <input
            type="text"
            placeholder={isAr ? 'مثال: شيك رقم 458921 بنك التجاري' : 'Ex: Chèque N° 458921 Attijariwafa'}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold transition"
          >
            {isAr ? 'إلغاء' : 'Annuler'}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold transition shadow-md shadow-emerald-500/20 flex items-center gap-2"
          >
            {submitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {isAr ? 'تسجيل الأداء' : 'Valider le paiement'}
          </button>
        </div>

      </form>
    </Modal>
  );
}
